import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Building2,
  Plus,
  Edit2,
  Trash2,
  Search,
  Save,
  X,
  MapPin,
  Briefcase,
  Building,
  CheckCircle,
  Stethoscope,
  Shield,
  CreditCard,
  Activity,
  Navigation
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { formatCNPJ, formatCEP, formatPhone, onlyNumbers, validateCEP, validatePhone, validateDateBR } from '../lib/helpers';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from './ui/table';
import { Checkbox } from './ui/checkbox';
import CompanyCertificatesSection from './company/CompanyCertificatesSection';
import EsocialCadastroSection from './company/EsocialCadastroSection';
import EsocialWizard from './company/EsocialWizard';
import EsocialNotificationsSection from './company/EsocialNotificationsSection';

const API = process.env.REACT_APP_BACKEND_URL;
const EMPTY_SELECT_VALUE = '__none__';

const CompanyManagement = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [acordos, setAcordos] = useState([]);
  const [jornadas, setJornadas] = useState([]);
  const [convencoes, setConvencoes] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    razao_social: '',
    cnpj: '',
    inscricao_estadual: '',
    inscricao_estadual_isento: false,
    inscricao_municipal: '',
    regime_tributario: '',
    data_constituicao: '',
    cep: '',
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    estado: '',
    pais: 'Brasil',
    telefone: '',
    email: '',
    convencao_coletiva_padrao_id: '',
    acordo_banco_horas_id: '',
    jornada_id: ''
  });

  useEffect(() => {
    fetchCompanies();
    fetchAcordosJornadas();
  }, []);

  const fetchAcordosJornadas = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [acordosRes, jornadasRes, convencoesRes] = await Promise.all([
        axios.get(`${API}/api/banco-horas/acordos`, { headers }),
        axios.get(`${API}/api/banco-horas/jornadas`, { headers }),
        axios.get(`${API}/api/banco-horas/convencoes-disponiveis`, { headers })
      ]);
      setAcordos(acordosRes.data);
      setJornadas(jornadasRes.data);
      setConvencoes(convencoesRes.data);
    } catch (error) {
      console.error('Erro ao carregar acordos/jornadas:', error);
    }
  };

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanies(response.data);
    } catch (error) {
      console.error('Erro ao carregar empresas:', error);
      toast.error('Erro ao carregar empresas');
    } finally {
      setLoading(false);
    }
  };

  const buscarCEP = async (cep) => {
    const cepLimpo = cep.replace(/\D/g, '');
    if (cepLimpo.length !== 8) return;

    try {
      const response = await axios.get(`https://viacep.com.br/ws/${cepLimpo}/json/`);
      if (response.data.erro) {
        toast.error('CEP não encontrado');
        return;
      }

      setFormData(prev => ({
        ...prev,
        logradouro: response.data.logradouro || '',
        bairro: response.data.bairro || '',
        cidade: response.data.localidade || '',
        estado: response.data.uf || '',
        complemento: response.data.complemento || prev.complemento
      }));
      
      toast.success('Endereço preenchido automaticamente');
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
      toast.error('Erro ao buscar CEP');
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const buscarCNPJ = async (cnpj) => {
    const cnpjLimpo = cnpj.replace(/\D/g, '');
    if (cnpjLimpo.length !== 14) {
      toast.error('CNPJ incompleto');
      return;
    }
    try {
      const { data } = await axios.get(`https://brasilapi.com.br/api/cnpj/v1/${cnpjLimpo}`);
      setFormData(prev => ({
        ...prev,
        razao_social: data.razao_social || '',
        name: data.nome_fantasia || data.razao_social || '',
        cep: (data.cep || '').replace(/\D/g, '').replace(/(\d{5})(\d)/, '$1-$2'),
        logradouro: data.logradouro || '',
        numero: data.numero || '',
        complemento: data.complemento || '',
        bairro: data.bairro || '',
        cidade: data.municipio || '',
        estado: data.uf || '',
      }));
      toast.success('Dados da empresa preenchidos automaticamente');
    } catch (error) {
      console.error('Erro ao buscar CNPJ:', error);
      toast.error('Erro ao buscar CNPJ — verifique se o CNPJ é válido');
    }
  };

  const openDialog = (company = null) => {
    if (company) {
      setSelectedCompany(company);
      setFormData({ ...company });
    } else {
      setSelectedCompany(null);
      setFormData({
        name: '',
        razao_social: '',
        cnpj: '',
        inscricao_estadual: '',
        inscricao_estadual_isento: false,
        inscricao_municipal: '',
        regime_tributario: '',
        data_constituicao: '',
        cep: '',
        logradouro: '',
        numero: '',
        complemento: '',
        bairro: '',
        cidade: '',
        estado: '',
        pais: 'Brasil',
        telefone: '',
        email: '',
        convencao_coletiva_padrao_id: '',
        acordo_banco_horas_id: '',
        jornada_id: ''
      });
    }
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setSelectedCompany(null);
  };

  const handleSave = async () => {
    if (!formData.name) {
      toast.error('Nome da empresa é obrigatório');
      return;
    }

    const cnpjDigits = onlyNumbers(formData.cnpj || '');
    if (cnpjDigits && cnpjDigits.length !== 14) {
      toast.error('CNPJ inválido. Deve conter 14 dígitos');
      return;
    }

    if (formData.cep && !validateCEP(formData.cep)) {
      toast.error('CEP inválido. Deve conter 8 dígitos');
      return;
    }

    if (formData.telefone) {
      const telDigits = onlyNumbers(formData.telefone);
      if (telDigits.length >= 10 && !validatePhone(telDigits)) {
        toast.error('Telefone inválido. Use o formato (11) 99999-9999');
        return;
      }
    }

    if (formData.data_constituicao && !validateDateBR(formData.data_constituicao, { futureYears: 0, pastYears: 120 })) {
      toast.error('Data de constituição inválida');
      return;
    }

    const cleanedData = {
      ...formData,
      cnpj: cnpjDigits,
      cep: onlyNumbers(formData.cep || ''),
      telefone: onlyNumbers(formData.telefone || ''),
      convencao_coletiva_padrao_id: formData.convencao_coletiva_padrao_id || null,
      acordo_banco_horas_id: formData.acordo_banco_horas_id || null,
      jornada_id: formData.jornada_id || null,
    };

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      if (selectedCompany) {
        // Atualizar
        await axios.put(`${API}/api/companies/${selectedCompany.id}`, cleanedData, { headers });
        toast.success('Empresa atualizada com sucesso');
      } else {
        // Criar
        await axios.post(`${API}/api/companies`, cleanedData, { headers });
        toast.success('Empresa criada com sucesso');
      }

      fetchCompanies();
      closeDialog();
    } catch (error) {
      console.error('Erro ao salvar empresa:', error);
      toast.error(error?.response?.data?.detail || 'Erro ao salvar empresa');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/companies/${selectedCompany.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Empresa excluída com sucesso');
      fetchCompanies();
      setDeleteDialogOpen(false);
      setSelectedCompany(null);
    } catch (error) {
      console.error('Erro ao excluir empresa:', error);
      toast.error('Erro ao excluir empresa');
    } finally {
      setLoading(false);
    }
  };

  const filteredCompanies = companies.filter(company =>
    company.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    company.razao_social?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    company.cnpj?.includes(searchTerm)
  );

  const location = useLocation();
  const navigate = useNavigate();

  const cadastroTabs = [
    { key: 'companies', label: 'Empresas', icon: Building2, route: '/cadastros/empresas' },
    { key: 'branches', label: 'Filiais', icon: MapPin, route: '/cadastros/filiais' },
    { key: 'positions', label: 'Cargos e Salários', icon: Briefcase, route: '/configuracoes/cargos-salarios' },
    { key: 'departments', label: 'Departamentos', icon: Building, route: '/cadastros/departamentos' },
    { key: 'statuses', label: 'Situações', icon: CheckCircle, route: '/cadastros/situacoes' },
    { key: 'odonto', label: 'Odonto', icon: Stethoscope, route: '/cadastros/odonto' },
    { key: 'segVida', label: 'Seg. Vida', icon: Shield, route: '/cadastros/seg-vida' },
    { key: 'totalPass', label: 'Total Pass', icon: CreditCard, route: '/cadastros/total-pass' },
    { key: 'medOcupacional', label: 'Med. Ocupacional', icon: Activity, route: '/cadastros/med-ocupacional' },
    { key: 'companyAddresses', label: 'Endereços', icon: Navigation, route: '/cadastros/enderecos' },
  ];

  return (
    <div className="space-y-6">
      {/* Tab navigation */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-2">
            {cadastroTabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = location.pathname === tab.route;
              return (
                <Button
                  key={tab.key}
                  variant={isActive ? "default" : "outline"}
                  onClick={() => navigate(tab.route)}
                  className={`flex items-center space-x-2 ${isActive ? 'btn-primary' : ''}`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{tab.label}</span>
                </Button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center space-x-2">
                <Building2 className="h-5 w-5" />
                <span>Gestão de Empresas</span>
              </CardTitle>
              <CardDescription>
                Cadastro completo de empresas para geração de AFD e relatórios
              </CardDescription>
            </div>
            <Button onClick={() => openDialog()}>
              <Plus className="h-4 w-4 mr-2" />
              Nova Empresa
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Busca */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Buscar por nome, razão social ou CNPJ..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Tabela */}
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome Fantasia</TableHead>
                  <TableHead>Razão Social</TableHead>
                  <TableHead>CNPJ</TableHead>
                  <TableHead>Regime Tributário</TableHead>
                  <TableHead>Convenção</TableHead>
                  <TableHead>Jornada</TableHead>
                  <TableHead>Cidade/Estado</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8">
                      Carregando...
                    </TableCell>
                  </TableRow>
                ) : filteredCompanies.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                      Nenhuma empresa encontrada
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCompanies.map((company) => {
                    const acordoNome = acordos.find(a => a.id === company.acordo_banco_horas_id)?.nome;
                    const convencaoNome = convencoes.find(c => c.id === company.convencao_coletiva_padrao_id)?.nome;
                    const jornadaNome = jornadas.find(j => j.id === company.jornada_id)?.nome;
                    return (
                      <TableRow key={company.id}>
                        <TableCell className="font-medium">{company.name}</TableCell>
                        <TableCell>{company.razao_social || '-'}</TableCell>
                        <TableCell>{company.cnpj || '-'}</TableCell>
                        <TableCell>{company.regime_tributario || '-'}</TableCell>
                        <TableCell>{convencaoNome || '-'}</TableCell>
                        <TableCell>{jornadaNome || '-'}</TableCell>
                        <TableCell>{company.cidade && company.estado ? `${company.cidade}/${company.estado}` : '-'}</TableCell>
                        <TableCell className="text-right space-x-2">
                          <Button variant="outline" size="sm" onClick={() => openDialog(company)}>
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button variant="destructive" size="sm" onClick={() => { setSelectedCompany(company); setDeleteDialogOpen(true); }}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Dialog de Cadastro/Edição */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedCompany ? 'Editar Empresa' : 'Nova Empresa'}
            </DialogTitle>
            <DialogDescription>
              Preencha os dados completos da empresa. Campos obrigatórios para geração de AFD estão marcados com *.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-6 py-4">
            {/* Dados Básicos */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">Dados Básicos</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nome Fantasia *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Nome da empresa"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="razao_social">Razão Social *</Label>
                  <Input
                    id="razao_social"
                    value={formData.razao_social}
                    onChange={(e) => handleInputChange('razao_social', e.target.value)}
                    placeholder="Razão social"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cnpj">CNPJ *</Label>
                  <div className="flex space-x-2">
                    <Input
                      id="cnpj"
                      value={formData.cnpj}
                      onChange={(e) => handleInputChange('cnpj', formatCNPJ(e.target.value))}
                      placeholder="00.000.000/0000-00"
                      maxLength={18}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => buscarCNPJ(formData.cnpj)}
                      disabled={!formData.cnpj || formData.cnpj.replace(/\D/g, '').length !== 14}
                      title="Buscar CNPJ"
                    >
                      <Search className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="data_constituicao">Data de Constituição</Label>
                  <Input
                    id="data_constituicao"
                    type="date"
                    value={formData.data_constituicao}
                    onChange={(e) => handleInputChange('data_constituicao', e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Inscrições */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">Inscrições e Regime</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="inscricao_estadual">Inscrição Estadual</Label>
                  <Input
                    id="inscricao_estadual"
                    value={formData.inscricao_estadual}
                    onChange={(e) => handleInputChange('inscricao_estadual', e.target.value)}
                    placeholder="000.000.000.000"
                    disabled={formData.inscricao_estadual_isento}
                  />
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="isento"
                      checked={formData.inscricao_estadual_isento}
                      onCheckedChange={(checked) => {
                        handleInputChange('inscricao_estadual_isento', checked);
                        if (checked) handleInputChange('inscricao_estadual', 'ISENTO');
                      }}
                    />
                    <label htmlFor="isento" className="text-sm text-gray-600 cursor-pointer">
                      Isento
                    </label>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="inscricao_municipal">Inscrição Municipal</Label>
                  <Input
                    id="inscricao_municipal"
                    value={formData.inscricao_municipal}
                    onChange={(e) => handleInputChange('inscricao_municipal', e.target.value)}
                    placeholder="000.000.000-0"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="regime_tributario">Regime Tributário *</Label>
                <Select
                  value={formData.regime_tributario}
                  onValueChange={(value) => handleInputChange('regime_tributario', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o regime tributário" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Simples Nacional">Simples Nacional</SelectItem>
                    <SelectItem value="Lucro Presumido">Lucro Presumido</SelectItem>
                    <SelectItem value="Lucro Real">Lucro Real</SelectItem>
                    <SelectItem value="MEI">MEI</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Endereço */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">Endereço</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cep">CEP *</Label>
                  <div className="flex space-x-2">
                    <Input
                      id="cep"
                      value={formData.cep}
                      onChange={(e) => handleInputChange('cep', formatCEP(e.target.value))}
                      placeholder="00000-000"
                      maxLength={9}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => buscarCEP(formData.cep)}
                      disabled={!formData.cep || formData.cep.replace(/\D/g, '').length !== 8}
                      title="Buscar CEP"
                    >
                      <Search className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="space-y-2 col-span-2">
                  <Label htmlFor="logradouro">Logradouro *</Label>
                  <Input
                    id="logradouro"
                    value={formData.logradouro}
                    onChange={(e) => handleInputChange('logradouro', e.target.value)}
                    placeholder="Rua, Avenida, etc"
                  />
                </div>
              </div>

              <div className="grid grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="numero">Número *</Label>
                  <Input
                    id="numero"
                    value={formData.numero}
                    onChange={(e) => handleInputChange('numero', e.target.value)}
                    placeholder="123"
                  />
                </div>
                <div className="space-y-2 col-span-3">
                  <Label htmlFor="complemento">Complemento</Label>
                  <Input
                    id="complemento"
                    value={formData.complemento}
                    onChange={(e) => handleInputChange('complemento', e.target.value)}
                    placeholder="Sala, Andar, etc"
                  />
                </div>
              </div>

              <div className="grid grid-cols-4 gap-4">
                <div className="space-y-2 col-span-2">
                  <Label htmlFor="bairro">Bairro *</Label>
                  <Input
                    id="bairro"
                    value={formData.bairro}
                    onChange={(e) => handleInputChange('bairro', e.target.value)}
                    placeholder="Bairro"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cidade">Cidade *</Label>
                  <Input
                    id="cidade"
                    value={formData.cidade}
                    onChange={(e) => handleInputChange('cidade', e.target.value)}
                    placeholder="Cidade"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="estado">Estado *</Label>
                  <Input
                    id="estado"
                    value={formData.estado}
                    onChange={(e) => handleInputChange('estado', e.target.value.toUpperCase())}
                    placeholder="UF"
                    maxLength={2}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="pais">País</Label>
                <Input
                  id="pais"
                  value={formData.pais}
                  onChange={(e) => handleInputChange('pais', e.target.value)}
                  placeholder="Brasil"
                />
              </div>
            </div>

            {/* Contato */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">Contato</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="telefone">Telefone</Label>
                  <Input
                    id="telefone"
                    value={formData.telefone}
                    onChange={(e) => handleInputChange('telefone', formatPhone(e.target.value))}
                    placeholder="(00) 00000-0000"
                    maxLength={15}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">E-mail</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="contato@empresa.com"
                  />
                </div>
              </div>
            </div>

            {/* Parâmetros Trabalhistas */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">Parâmetros Trabalhistas</h3>
              <p className="text-xs text-slate-500">
                Configure a convenção coletiva padrão da empresa, o acordo de banco de horas e a jornada padrão.
                A convenção definida aqui é a base usada para herança no cadastro trabalhista do colaborador e para regras como desconto sindical.
              </p>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="convencao_coletiva_padrao_id">Convenção Coletiva Padrão</Label>
                  <Select
                    value={formData.convencao_coletiva_padrao_id || EMPTY_SELECT_VALUE}
                    onValueChange={(value) => handleInputChange('convencao_coletiva_padrao_id', value === EMPTY_SELECT_VALUE ? '' : value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione a convenção" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={EMPTY_SELECT_VALUE}>Nenhuma</SelectItem>
                      {convencoes.map((convencao) => (
                        <SelectItem key={convencao.id} value={convencao.id}>
                          {convencao.nome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="acordo_banco_horas_id">Acordo de Banco de Horas</Label>
                  <Select
                    value={formData.acordo_banco_horas_id || EMPTY_SELECT_VALUE}
                    onValueChange={(value) => handleInputChange('acordo_banco_horas_id', value === EMPTY_SELECT_VALUE ? '' : value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o acordo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={EMPTY_SELECT_VALUE}>Nenhum</SelectItem>
                      {acordos.map((acordo) => (
                        <SelectItem key={acordo.id} value={acordo.id}>
                          {acordo.nome}
                          {acordo.convencao_coletiva_nome ? ` · ${acordo.convencao_coletiva_nome}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="jornada_id">Jornada Padrão</Label>
                  <Select
                    value={formData.jornada_id || EMPTY_SELECT_VALUE}
                    onValueChange={(value) => handleInputChange('jornada_id', value === EMPTY_SELECT_VALUE ? '' : value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione a jornada" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={EMPTY_SELECT_VALUE}>Nenhum</SelectItem>
                      {jornadas.map((jornada) => (
                        <SelectItem key={jornada.id} value={jornada.id}>
                          {jornada.nome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* eSocial — Wizard Onboarding (visão geral do progresso) */}
            <div className="space-y-4">
              <EsocialWizard
                companyId={selectedCompany?.id}
                onJumpToCert={() => {
                  document.getElementById('certificates-section')?.scrollIntoView({
                    behavior: 'smooth', block: 'start',
                  });
                }}
                onJumpToCadastro={(tipo) => {
                  const target = document.querySelector(
                    `[data-testid="esocial-cadastro-${tipo}-new-btn"]`,
                  );
                  if (target) {
                    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    setTimeout(() => target.click(), 600);
                  }
                }}
              />
            </div>

            {/* Certificados Digitais (e-CNPJ / e-CPF) */}
            <div className="space-y-4" id="certificates-section">
              <div>
                <h3 className="font-semibold text-lg">Certificados Digitais</h3>
                <p className="text-xs text-slate-500">
                  Carregue o e-CNPJ da empresa e o e-CPF do representante legal para assinatura
                  e transmissão eSocial.
                </p>
              </div>
              <CompanyCertificatesSection companyId={selectedCompany?.id} />
            </div>

            {/* eSocial — Cadastro Inicial (S-1000 / S-1005 / S-1010) */}
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-lg">eSocial — Cadastro Inicial</h3>
                <p className="text-xs text-slate-500">
                  Antes de enviar qualquer evento ao gov.br é obrigatório transmitir o S-1000.
                  Depois S-1005 (estabelecimento) e S-1010 (rubricas).
                </p>
              </div>
              <EsocialCadastroSection companyId={selectedCompany?.id} />
            </div>

            <EsocialNotificationsSection companyId={selectedCompany?.id} />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>
              <X className="h-4 w-4 mr-2" />
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={loading}>
              <Save className="h-4 w-4 mr-2" />
              {loading ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Exclusão */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Exclusão</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir a empresa <strong>{selectedCompany?.name}</strong>?
              Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={loading}>
              {loading ? 'Excluindo...' : 'Excluir'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompanyManagement;
